/* eslint-disable @typescript-eslint/no-unused-vars, no-unused-vars */
import { useSSNMask, useZipCodeMask } from '../index.js';
import type { UseMaskT } from '../index.js';

// test we expose the namespace and the namespace follows our deliverable conventions
type ComponentPropsForApp = UseMaskT.Props;
type ComponentPropsInternals = UseMaskT.InternalProps;
type ComponentPropsDefaultProps = UseMaskT.DefaultProps;
type ComponentPropsOptionalProps = UseMaskT.OptionalProps;
type ComponentPropsRequiredProps = UseMaskT.RequiredProps;

const testRequiredProps: ComponentPropsRequiredProps = {
  valueSetter: () => {},
};

const testOptionalProps: ComponentPropsOptionalProps = {};

// difference Props and InternalProps is that InternalProps has all the default props filled in
// Props allows for partial defaults
const testPartialDefaults: Partial<ComponentPropsDefaultProps> = {
  onChange: () => {},
  onKeyDown: () => {},
};

const testProps: ComponentPropsForApp = {
  ...testRequiredProps,
  ...testOptionalProps,
  ...testPartialDefaults,
};

const testPropsAsSyntax = {
  ...testRequiredProps,
  ...testOptionalProps,
  ...testPartialDefaults,
} as ComponentPropsForApp;

// InternalProps requires all defaults to be filled in
const testCompleteDefaults: Required<ComponentPropsDefaultProps> = {
  onChange: () => {},
  onKeyDown: () => {},
  onBlur: () => {},
};

const testInternalProps: ComponentPropsInternals = {
  ...testRequiredProps,
  ...testOptionalProps,
  ...testCompleteDefaults,
};

const testInternalPropsAsSyntax = {
  ...testRequiredProps,
  ...testOptionalProps,
  ...testCompleteDefaults,
} as ComponentPropsInternals;

// using the explicit type definition, if there is an error, it will be marked on the key that is wrong
const testExplicitDefinition: ComponentPropsForApp = {
  valueSetter: () => {},
  onChange: () => {},
  onKeyDown: () => {},
  onBlur: () => {},
};

// using the "as" syntax, if there is an error, it will be marking the whole object as wrong because it is not compatible with the type
const testInferedTypeCompatibility = {
  valueSetter: () => {},
  onChange: () => {},
  onKeyDown: () => {},
  onBlur: () => {},
} as ComponentPropsForApp;

const testDefinitionAsConst = {
  valueSetter: () => {},
  onChange: () => {},
  onKeyDown: () => {},
  onBlur: () => {},
} as const;

const ExampleUsageComponent = () => {
  // works with explicitly casted props, all syntaxes
  const inputPropsSSN = useSSNMask({
    ...testExplicitDefinition,
  });
  const inputPropsSSN2 = useSSNMask({
    ...testInferedTypeCompatibility,
  });
  const inputPropsSSN3 = useSSNMask({
    ...testDefinitionAsConst,
  });
  // works with inline values
  const { onChange, onKeyDown } = useSSNMask({
    valueSetter: () => {},
    onChange: () => {},
    onKeyDown: () => {},
    onBlur: () => {},
  });

  // works with explicitly casted props, all syntaxes
  const inputPropsZipCode = useZipCodeMask({
    ...testExplicitDefinition,
  });
  const inputPropsZipCode2 = useZipCodeMask({
    ...testInferedTypeCompatibility,
  });
  const inputPropsZipCode3 = useZipCodeMask({
    ...testDefinitionAsConst,
  });
  // works with inline values
  const { onChange: onChange2, onKeyDown: onKeyDown2 } = useZipCodeMask({
    valueSetter: () => {},
    onChange: () => {},
    onKeyDown: () => {},
    onBlur: () => {},
  });
  return <div></div>;
};
